import { ConnectivitySnackbar } from '@dmc/ui';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import {
  AssetDeviceTypeCellRenderer,
  assetDeviceTypeValueGetter,
} from '@casops/components/tables/cells/asset-device-type/AssetDeviceTypeCellRenderer';
import SoftwareStatusCellRenderer from '@casops/components/tables/cells/software-status/SoftwareStatusCellRenderer';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { ModelName } from '@casops/store/api';
import {
  AssetDeviceType,
  RegisteredAssetBasicModel,
  RegisteredAssetModel,
  useListAssetsQuery,
} from '@casops/store/apis/registeredAssetsApi';
import GridActionButton from '@casops/components/inputs/grid-action-button/GridActionButton';
import { skipToken } from '@reduxjs/toolkit/query';
import { CellClickedEvent, ColDef, IsRowSelectable } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Permission } from '@casops/permissions';
import { useAppDispatch } from '@casops/store/hooks';
import { openSidePanel, SidePanel } from '@casops/store/slices/globalSlice';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';

type Model = RegisteredAssetModel;

const modelName = ModelName.ConfigurationGroupAsset;
const permission = Permission.AssetManagement;

const getRowId = (data: RegisteredAssetBasicModel) => String(data.id);
const onRowClick = () => undefined; // Do nothing. Just an easy way to make the cursor a pointer for the whole row.

// A configuration group should never contain PPU assets, but the row selection guard is here just in case.
const isRowSelectable: IsRowSelectable<Model> = ({ data }): boolean =>
  data?.hw !== AssetDeviceType.PPU;

export function ConfigurationGroupAssets() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { configGroupId } = useParams();
  const dispatch = useAppDispatch();

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    return [
      {
        field: 'n',
        headerName: t('Fields.AssetName'),
        sort: 'desc',
        minWidth: 110,
      },
      {
        headerName: t('Fields.DeviceType'),
        minWidth: 110,
        valueGetter: assetDeviceTypeValueGetter,
        cellRenderer: AssetDeviceTypeCellRenderer,
      },
      { field: 'did', headerName: t('Fields.DeviceId'), minWidth: 150 },
      {
        colId: 'LastUpdated',
        field: 'ts',
        headerName: t('Fields.LastUpdated'),
        minWidth: 180,
        ...absoluteTimeColDef,
      },
      {
        field: 'stC',
        headerName: t('Fields.SoftwareStatus'),
        minWidth: 130,
        cellRenderer: SoftwareStatusCellRenderer,
      },
    ];
  }, [t, absoluteTimeColDef]);

  const queryParams = configGroupId ? { query: { configGroups: [configGroupId] } } : skipToken;

  const { data, ...requestRemainder } = useListAssetsQuery(queryParams, {
    refetchOnMountOrArgChange: 60,
  });

  const { items } = data ?? {};
  const fetchError = requestRemainder.error;
  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { hasData, noDataDescription } = useTableData<Model>({
    data: items,
    ...requestRemainder,
    noResultsDescription,
  });

  const onClickShowAssetsAssignConfigurationGroupPanel = useCallback(
    (ids: number[]) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.AssetsAssignConfigurationGroup,
          bulkModelIds: ids.map(String),
          bulkEditMode: true,
        }),
      );
    },
    [dispatch],
  );

  // The cell click is used in place of a row click.
  const onCellClicked = useCallback(
    (event: CellClickedEvent<Model>) => {
      if (event.data) {
        navigate(`/assets/asset/${event.data.id}/configuration`);
      }
    },
    [navigate],
  );

  // Applying an onCellClicked to every cell acts much like a row click, but automatically avoids the selection checkbox cell.
  const defaultColDef = useMemo<ColDef<Model>>(
    () => ({
      onCellClicked,
    }),
    [onCellClicked],
  );

  const sliceKey = `${modelName}--${configGroupId}`; // Create a unique sliceKey for each config group.

  return (
    <>
      <CollectionView<Model>
        sliceKey={sliceKey}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={items}
        hasData={hasData}
        noDataDescription={noDataDescription}
        defaultColDef={defaultColDef}
        onRowClick={onRowClick}
        getRowId={getRowId}
        rowSelectMode="multiRow"
        isRowSelectable={isRowSelectable}
        includeHiddenColumnsInQuickFilter={true}
        actions={
          <GridActionButton
            sliceKey={sliceKey}
            permission={permission}
            onClick={onClickShowAssetsAssignConfigurationGroupPanel}
            icon="edit"
            label={t('Assign')}
            disabled={!items?.length}
          />
        }
      />
      <ConnectivitySnackbar
        error={fetchError}
        message={t('Model.Fetch.Error', { modelName })}
        title={t('Error')}
      />
    </>
  );
}

export default ConfigurationGroupAssets;
